import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth, API, formatApiError } from "../contexts/AuthContext";
import "../lms.css";

export default function ChangePasswordPage() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== pw2) return setError("The two passwords don't match.");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { new_password: pw });
      const fresh = await refreshMe();
      const role = fresh?.role || user?.role;
      navigate(role === "admin" ? "/admin" : role === "mentor" ? "/mentor" : "/student", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lms-root min-h-screen flex flex-col">
      <div aria-hidden className="fixed inset-0 lms-grain" />
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
          className="w-full max-w-md lms-glass-strong p-8 md:p-10"
          data-testid="change-password-card">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={16} className="text-[#7df5c8]" />
            <div className="lms-mono text-[#5ceeff]">— Secure your account</div>
          </div>
          <h1 className="lms-display text-white text-3xl md:text-4xl leading-[1.05] mb-2">
            Set a new password.
          </h1>
          <p className="text-white/55 text-sm mt-2 mb-7 leading-relaxed">
            Welcome, <span className="text-white/80">{user?.name || user?.email}</span>. For your safety, please choose a new password you'll remember.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
            <div>
              <label className="lms-label">New password</label>
              <div className="relative">
                <input type={show ? "text" : "password"} className="lms-input pr-12"
                  placeholder="At least 8 characters"
                  value={pw} onChange={e => setPw(e.target.value)} required minLength={8}
                  autoFocus data-testid="change-password-new" />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition"
                  aria-label={show ? "Hide" : "Show"}>
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="lms-label">Confirm new password</label>
              <input type={show ? "text" : "password"} className="lms-input"
                placeholder="Re-enter your new password"
                value={pw2} onChange={e => setPw2(e.target.value)} required minLength={8}
                data-testid="change-password-confirm" />
            </div>

            {error && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                className="p-3 rounded-[14px] border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm"
                data-testid="change-password-error">
                {error}
              </motion.div>
            )}

            <button type="submit" disabled={loading}
              className="lms-btn lms-btn-primary w-full justify-center h-12 text-[15px] disabled:opacity-50"
              data-testid="change-password-submit">
              {loading ? "Saving…" : "Save & continue"}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>

          <div className="lms-divider my-6" />
          <button onClick={logout} className="text-center w-full text-xs text-white/45 hover:text-white/75 transition" data-testid="change-password-logout">
            Not you? Sign out.
          </button>
        </motion.div>
      </div>
      <div aria-hidden className="fixed bottom-[-30%] left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.14), transparent 60%)", filter: "blur(60px)" }} />
    </div>
  );
}
